import { useState } from "react";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import RichTextEditor from "./RichTextEditor";
import { Input } from "../ui/input";

const emptyPost = { id: null, title: "", excerpt: "", cover_image: "", tags: "", status: "draft", content: "" };

export default function AdminBlogTab({ posts, reload }) {
  const [post, setPost] = useState(emptyPost);
  const [postOpen, setPostOpen] = useState(false);

  const openNewPost = () => { setPost(emptyPost); setPostOpen(true); };
  const editPost = (p) => { setPost({ id: p.id, title: p.title, excerpt: p.excerpt, cover_image: p.cover_image, tags: (p.tags || []).join(", "), status: p.status, content: p.content }); setPostOpen(true); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const savePost = async (status) => {
    try {
      const body = { title: post.title, excerpt: post.excerpt, cover_image: post.cover_image, content: post.content, status, tags: post.tags.split(",").map((x) => x.trim()).filter(Boolean) };
      if (post.id) await api.put(`/blog/${post.id}`, body);
      else await api.post("/blog", body);
      toast.success(status === "published" ? "Published." : "Saved as draft."); setPostOpen(false); setPost(emptyPost); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed."); }
  };
  const delPost = async (id) => { await api.delete(`/blog/${id}`); toast.success("Post deleted."); reload(); };

  return (
    <div className="space-y-6">
      {postOpen ? (
        <div className="rounded-2xl border border-border glass p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-bold text-lg">{post.id ? "Edit post" : "New post"}</h3>
            <button onClick={() => { setPostOpen(false); setPost(emptyPost); }} className="w-8 h-8 rounded-lg border border-border flex items-center justify-center"><X className="w-4 h-4" /></button>
          </div>
          <Input data-testid="post-title" placeholder="Title" value={post.title} onChange={(e) => setPost({ ...post, title: e.target.value })} className="h-12 text-lg" />
          <Input data-testid="post-excerpt" placeholder="Excerpt / summary" value={post.excerpt} onChange={(e) => setPost({ ...post, excerpt: e.target.value })} />
          <div className="grid sm:grid-cols-2 gap-3">
            <Input data-testid="post-cover" placeholder="Cover image URL" value={post.cover_image} onChange={(e) => setPost({ ...post, cover_image: e.target.value })} />
            <Input data-testid="post-tags" placeholder="Tags (comma separated)" value={post.tags} onChange={(e) => setPost({ ...post, tags: e.target.value })} />
          </div>
          <RichTextEditor key={post.id || "new"} value={post.content} onChange={(html) => setPost((p) => ({ ...p, content: html }))} />
          <div className="flex gap-3">
            <button data-testid="post-publish" onClick={() => savePost("published")} className="rounded-full bg-primary text-primary-foreground font-semibold px-6 py-2.5 text-sm">Publish</button>
            <button data-testid="post-draft" onClick={() => savePost("draft")} className="rounded-full border border-border px-6 py-2.5 text-sm">Save draft</button>
          </div>
        </div>
      ) : (
        <button data-testid="new-post" onClick={openNewPost} className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold px-6 py-3 text-sm"><Plus className="w-4 h-4" /> New post</button>
      )}
      <div className="rounded-2xl border border-border overflow-hidden">
        {posts.map((p) => (
          <div key={p.id} data-testid={`post-row-${p.slug}`} className="flex items-center justify-between px-5 py-4 border-b border-border last:border-0">
            <div>
              <div className="font-semibold flex items-center gap-2">{p.title}
                <span className={`font-mono-x text-[10px] tracking-widest px-2 py-0.5 rounded-full ${p.status === "published" ? "bg-emerald-500/10 text-emerald-600" : "bg-amber-500/10 text-amber-600"}`}>{p.status}</span>
              </div>
              <div className="text-sm text-muted-foreground">{p.read_minutes} min · {(p.tags || []).join(", ")}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => editPost(p)} className="w-9 h-9 rounded-lg border border-border flex items-center justify-center hover:bg-secondary"><Edit3 className="w-4 h-4" /></button>
              <button data-testid={`post-del-${p.slug}`} onClick={() => delPost(p.id)} className="w-9 h-9 rounded-lg border border-border flex items-center justify-center hover:bg-red-500/10 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
